import Dexie, { type EntityTable } from "dexie";
import type { RecipeApiItem, TaxonomyApiItem, MeResponse, CookSessionApiItem, ShoppingListApiItem, ItemCatalogApiItem, MealPlanApiItem, ShelfConfig } from "@recipevault/shared";

// "me" gets a single canonical row keyed by `_singleton: "me"`.
export interface MeCacheRow extends MeResponse {
  _singleton: "me";
}

// Household-shared shelf-config document gets a single canonical row keyed
// by `_singleton: "config"` — same pattern as MeCacheRow above.
export interface ShelfConfigRow extends ShelfConfig {
  _singleton: "config";
}

export interface SyncStateRow {
  entity:
    | "recipes"
    | "taxonomy"
    | "me"
    | "cookSessions"
    | "shoppingLists"
    | "itemCatalog"
    | "mealPlans"
    // Sentinel row: `lastSyncAt` carries the last-synced household id, not a
    // timestamp. Used by SyncService to detect a household switch and force a
    // wipe + re-bootstrap so a second user never sees the other household's cache.
    | "__household";
  lastSyncAt: string; // ISO 8601 UTC Z
}

export class RecipeVaultDB extends Dexie {
  recipes!: EntityTable<RecipeApiItem, "id">;
  taxonomy!: EntityTable<TaxonomyApiItem, "id">;
  me!: EntityTable<MeCacheRow, "_singleton">;
  sync_state!: EntityTable<SyncStateRow, "entity">;
  cookSessions!: EntityTable<CookSessionApiItem, "id">;
  shoppingLists!: EntityTable<ShoppingListApiItem, "id">;
  itemCatalog!: EntityTable<ItemCatalogApiItem, "normalizedName">;
  mealPlans!: EntityTable<MealPlanApiItem, "weekStartDate">;
  shelfConfig!: EntityTable<ShelfConfigRow, "_singleton">;

  // `name` defaults to "recipevault" (the real app database) — the parameter
  // exists solely so tests can open an isolated, differently-named IndexedDB
  // database (e.g. to exercise the v4 -> v5 upgrade path without touching the
  // shared `db` singleton below).
  constructor(name = "recipevault") {
    super(name);
    // schema v1 — bumped from Plan 03's placeholder.
    this.version(1).stores({
      recipes: "&id, updatedAt, deletedAt",
      taxonomy: "&id, kind, updatedAt, deletedAt",
      me: "&_singleton",
      sync_state: "&entity",
    });
    // schema v2 — CookSessions (Plan 06c). ShoppingLists in Plan 09, ImportJobs in Plan 08.
    this.version(2).stores({
      recipes: "&id, updatedAt, deletedAt",
      taxonomy: "&id, kind, updatedAt, deletedAt",
      me: "&_singleton",
      sync_state: "&entity",
      cookSessions: "&id, recipeId, status, startedAt",
    });
    // schema v3 — ShoppingLists + ItemCatalog (Plan 10).
    this.version(3).stores({
      recipes: "&id, updatedAt, deletedAt",
      taxonomy: "&id, kind, updatedAt, deletedAt",
      me: "&_singleton",
      sync_state: "&entity",
      cookSessions: "&id, recipeId, status, startedAt",
      shoppingLists: "&id, updatedAt, deletedAt, status",
      itemCatalog: "&normalizedName, category, updatedAt",
    });
    // schema v4 — MealPlans (Meal Planning Slice A).
    this.version(4).stores({
      recipes: "&id, updatedAt, deletedAt",
      taxonomy: "&id, kind, updatedAt, deletedAt",
      me: "&_singleton",
      sync_state: "&entity",
      cookSessions: "&id, recipeId, status, startedAt",
      shoppingLists: "&id, updatedAt, deletedAt, status",
      itemCatalog: "&normalizedName, category, updatedAt",
      mealPlans: "&weekStartDate, updatedAt, deletedAt",
    });
    // schema v5 — ShelfConfig (Plan 13 Library Shelves). Single-row store,
    // keyed by the constant "_singleton" so puts always target the one row.
    // Unchanged stores from v4 don't need to be restated — Dexie carries them
    // forward automatically; only the new store is declared here.
    this.version(5).stores({
      shelfConfig: "&_singleton",
    });
  }
}

export const db = new RecipeVaultDB();

/** Clear EVERY table (logout, 401, household switch). One transaction so a
 *  racing sync tick can't observe a half-wiped DB. */
export async function wipeAllTables(): Promise<void> {
  await db.transaction("rw", db.tables, async () => {
    await Promise.all(db.tables.map((t) => t.clear()));
  });
}
